import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import CreditAnalytics from '../components/CreditAnalytics.jsx';
import UserMenu from '../components/UserMenu.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import useSessionPersistence from '../hooks/useSessionPersistence.jsx';
import api from '../utils/api.js';
import { handleApiError } from '../utils/api.js';
import { createLogger } from '../utils/logger.js';

const log = createLogger('Dashboard');
import CreditTracker from '../components/CreditTracker.jsx';
import SessionManager from '../components/SessionManager.jsx';
import ReferralSection from '../components/ReferralSection.jsx';
import SecurityTab from '../components/dashboard/SecurityTab.jsx';
import { formatUsageTime, formatRelativeTime, getTimezoneDisplayName } from '../utils/dateUtils.js';
import {
    useCreditBalance,
    useCreditHistory,
    useCreditAnalytics,
    usePricing,
    useUserSessions,
    useInvoices,
} from '../hooks/queries/index.js';
import {
    LogoText,
    Button,
    Card,
    Text,
    Heading,
    Container,
    NavLink,
    Alert,
    Spinner,
    Badge,
    MetricGrid,
    MetricCard,
    InternalSection,
    Icon,
    MobileBottomNav
} from '../components/ui/index.jsx';

// Helper component for truncated session ideas
const TruncatedSessionIdea = ({ idea, className = "text-sm text-gray-400 font-normal", icon }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    if (!idea) return null;

    const MAX_LENGTH = 100;
    const shouldTruncate = idea.length > MAX_LENGTH;
    const text = isExpanded || !shouldTruncate ? idea : `${idea.substring(0, MAX_LENGTH)}...`;

    return (
        <div className={`mt-1 ${className}`}>
            {icon && <Icon name={icon} size="xs" className="mr-2 inline-block -mt-0.5 shrink-0" />}
            <span className="break-words">{text}</span>
            {shouldTruncate && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                    }}
                    className="ml-2 text-[#7B61FF] hover:text-[#BA87FF] font-medium transition-colors inline-block text-sm"
                >
                    {isExpanded ? 'Show Less' : 'Read More'}
                </button>
            )}
        </div>
    );
};

export default function Dashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user: authUser, loading: authLoading, refreshUserProfile, authType, signOut } = useAuth();
    const { loadUserSessions, calculateCompletionPercentage } = useSessionPersistence();

    // Get active tab from URL query params or default to 'overview'
    const urlParams = new URLSearchParams(location.search);
    const [activeTab, setActiveTab] = useState(urlParams.get('tab') || 'overview');

    // ============================================
    // TanStack Query Hooks - Data fetches independently
    // Each query loads in parallel, UI updates progressively
    // ============================================
    const isAuthenticated = authUser && !authLoading && authType === 'authenticated';

    const {
        data: creditsResponse,
        isLoading: creditsLoading,
        error: creditsError,
        refetch: refetchCredits
    } = useCreditBalance({ enabled: isAuthenticated });

    const {
        data: monthlyAnalytics,
        isLoading: analyticsLoading
    } = useCreditAnalytics(
        { timeframe: 'monthly', includeComparison: true, includePredictions: true, includeFraudCheck: false },
        { enabled: isAuthenticated }
    );

    const {
        data: historyResponse,
        isLoading: historyLoading
    } = useCreditHistory({ limit: 20 }, { enabled: isAuthenticated });

    const {
        data: pricingResponse,
        isLoading: pricingLoading
    } = usePricing({ enabled: isAuthenticated });

    const {
        data: invoicesResponse,
        isLoading: invoicesLoading
    } = useInvoices({ limit: 10 }, { enabled: isAuthenticated });

    // Sessions use the existing hook (has its own logic)
    const [sessionsData, setSessionsData] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(true);

    // Load sessions separately (uses existing hook pattern)
    useEffect(() => {
        if (isAuthenticated) {
            setSessionsLoading(true);
            loadUserSessions({ limit: 200, includeAutoSave: false })
                .then(sessions => {
                    setSessionsData(sessions || []);
                })
                .catch(() => {
                    setSessionsData([]);
                })
                .finally(() => {
                    setSessionsLoading(false);
                });
        }
    }, [isAuthenticated]);

    // Update tab from URL params
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const tab = urlParams.get('tab');
        if (tab && ['overview', 'billing', 'usage', 'architectures', 'apps', 'referrals', 'security'].includes(tab)) {
            setActiveTab(tab);
        }
    }, [location.search]);

    // Derived state from query data
    const pricingConfig = pricingResponse?.success ? pricingResponse.pricing : null;
    const allSessions = sessionsData;
    const recentSessions = sessionsData.slice(0, 5);
    const latestSession = sessionsData.length > 0 ? sessionsData[0] : null;

    // Combined loading state - show content as soon as credits load
    const loading = creditsLoading;
    const error = creditsError?.message || null;

    // ============================================
    // Computed Data from Query Results
    // Derives dashboardData and billingData from query responses
    // ============================================

    // Calculate credits used from transaction data
    const calculateCreditsFromTransactions = useCallback((transactions, timeframe = 'thisMonth') => {
        if (!transactions || !Array.isArray(transactions)) return 0;

        const now = new Date();
        let startDate;

        if (timeframe === 'today') {
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (timeframe === 'thisMonth') {
            startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        } else {
            return 0;
        }

        return transactions
            .filter(t => {
                if (!t.amount || t.amount >= 0) return false;
                if (t.operationType === 'account_creation') return false;
                const transDate = new Date(t.created_at || t.date || t.createdAt);
                return transDate >= startDate && transDate <= now;
            })
            .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
    }, []);

    // Calculate step costs from transactions
    const calculateStepCosts = useCallback((transactions) => {
        const stepCosts = {};
        const stepCounts = {};

        transactions?.forEach(transaction => {
            let operationType = transaction.operationType || transaction.operation_type;
            if (operationType === 'prompts_batch') operationType = 'prompts';
            if (operationType === 'analysis_claude' || operationType === 'analysis_openai') operationType = 'analysis';
            if (operationType === 'diagram' || operationType === 'flow-diagram') operationType = 'flow_diagram';
            if (operationType && transaction.amount < 0) {
                const cost = Math.abs(transaction.amount);
                if (!stepCosts[operationType]) {
                    stepCosts[operationType] = 0;
                    stepCounts[operationType] = 0;
                }
                stepCosts[operationType] += cost;
                stepCounts[operationType] += 1;
            }
        });

        const averages = {};
        Object.keys(stepCosts).forEach(step => {
            averages[step] = stepCounts[step] > 0 ? (stepCosts[step] / stepCounts[step]) : 0;
        });
        return averages;
    }, []);

    // Calculate days until credit reset
    const calculateDaysToReset = useCallback((renewalDate) => {
        if (!renewalDate) return 0;
        const renewal = new Date(renewalDate);
        const now = new Date();
        const diffTime = renewal - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    }, []);

    // Derive dashboardData from query results (memoized)
    const dashboardData = useMemo(() => {
        if (!creditsResponse && !authUser) return null;

        const transactionData = historyResponse?.transactions || [];
        const creditsUsedToday = calculateCreditsFromTransactions(transactionData, 'today');
        const creditsUsedThisMonth = calculateCreditsFromTransactions(transactionData, 'thisMonth');
        const stepCostAverages = calculateStepCosts(transactionData);

        const userPlan = creditsResponse?.user?.plan || authUser?.plan || 'free';

        return {
            name: authUser?.displayName,
            email: authUser?.email || 'Guest User',
            plan: userPlan,
            credits: creditsResponse?.balance?.available ?? authUser?.credits ?? 0,
            creditsUsedToday: creditsResponse?.usage?.today?.credits || creditsUsedToday,
            creditsPurchasedToday: creditsResponse?.balance?.purchased ?? 0,
            nextRefill: creditsResponse?.subscription?.renewalDate || authUser?.subscription?.renewalDate,
            monthlyLimit: creditsResponse?.subscription?.monthlyCredits || 8,
            creditsUsedThisMonth: creditsResponse?.usage?.thisMonth?.credits || creditsUsedThisMonth,
            totalCreditsThisMonth: creditsResponse?.balance?.total ?? 10,
            stepCostAverages,
            nextReset: calculateDaysToReset(creditsResponse?.subscription?.renewalDate || authUser?.subscription?.renewalDate),
            subscription: creditsResponse?.subscription || authUser?.subscription,
            recommendations: creditsResponse?.recommendations || []
        };
    }, [creditsResponse, authUser, historyResponse, calculateCreditsFromTransactions, calculateStepCosts, calculateDaysToReset]);

    // Derive billingData from query results (memoized)
    const billingData = useMemo(() => {
        if (!creditsResponse) return null;

        const transactionData = historyResponse?.transactions || [];
        const creditsUsedThisMonth = calculateCreditsFromTransactions(transactionData, 'thisMonth');

        return {
            success: true,
            overview: {
                credits: {
                    current: creditsResponse?.balance?.total ?? 0,
                    available: creditsResponse?.balance?.available ?? 0,
                    used: creditsResponse?.balance?.used ?? 0,
                    subscription: creditsResponse?.balance?.subscription ?? 0,  // Monthly plan credits (expire on cancel)
                    purchased: creditsResponse?.balance?.purchased ?? 0         // Referrals + bonuses (keep forever)
                },
                subscription: {
                    planName: creditsResponse?.user?.plan || authUser?.plan || 'Free',
                    status: creditsResponse?.subscription?.status || 'active',
                    monthlyCredits: creditsResponse?.subscription?.monthlyCredits || 0,
                    isActive: creditsResponse?.subscription?.status === 'active',
                    renewalDate: creditsResponse?.subscription?.renewalDate || null
                },
                spending: {
                    thisMonth: {
                        spent: monthlyAnalytics?.analytics?.current?.totalCost || 0,
                        creditsUsed: creditsResponse?.usage?.thisMonth?.credits || monthlyAnalytics?.analytics?.current?.totalCredits || creditsUsedThisMonth
                    },
                    lifetime: {
                        totalSpent: monthlyAnalytics?.analytics?.current?.totalCost || 0,
                        totalPurchased: creditsResponse?.balance?.purchased ?? 0
                    }
                }
            },
            monthlyAnalytics,
            historyData: historyResponse,
            invoices: invoicesResponse?.invoices || [],
            invoicesSummary: invoicesResponse?.summary || null
        };
    }, [creditsResponse, authUser, monthlyAnalytics, historyResponse, invoicesResponse, calculateCreditsFromTransactions]);

    // Refresh function for manual refresh
    const refreshData = useCallback(() => {
        refreshUserProfile();
        refetchCredits();
    }, [refreshUserProfile, refetchCredits]);

    // Helper functions for data transformation - uses pricing from backend (single source of truth)
    const getMonthlyLimit = (planData) => {
        // Use actual plan limits from backend if available
        if (dashboardData?.planLimits?.currentPlan?.monthlyCredits) {
            return dashboardData.planLimits.currentPlan.monthlyCredits;
        }

        // Fallback to authUser plan data if available
        if (authUser?.planLimits?.currentPlan?.monthlyCredits) {
            return authUser.planLimits.currentPlan.monthlyCredits;
        }

        // Use pricing config from backend (single source of truth)
        // 🔧 FIX: Use authUser.plan (from subscription table) instead of stale subscriptionTier
        const plan = planData || dashboardData?.plan || authUser?.plan || 'free';
        if (pricingConfig?.plans) {
            const planConfig = pricingConfig.plans.find(p => p.id === plan?.toLowerCase());
            if (planConfig?.credits) {
                return planConfig.credits;
            }
        }

        // Last resort fallback (should rarely be needed)
        return 8; // Default to free tier (1 architecture)
    };

    const getBillingAmount = (planData) => {
        // Use actual billing amounts from backend if available
        if (dashboardData?.planLimits?.currentPlan?.price_monthly) {
            return dashboardData.planLimits.currentPlan.price_monthly;
        }

        // Use pricing config from backend (single source of truth)
        // 🔧 FIX: Use authUser.plan (from subscription table) instead of stale subscriptionTier
        const plan = planData || dashboardData?.plan || authUser?.plan || 'free';
        if (pricingConfig?.plans) {
            const planConfig = pricingConfig.plans.find(p => p.id === plan?.toLowerCase());
            if (planConfig?.monthlyPrice !== undefined) {
                return planConfig.monthlyPrice;
            }
        }

        // Last resort fallback (should rarely be needed)
        return 0; // Default to free
    };

    const handleNewProject = () => {
        navigate('/');
    };

    const handleBuyCredits = () => {
        navigate('/plans');
    };

    const handleUpgrade = () => {
        navigate('/plans');
    };

    const handleResumeSession = (sessionId) => {
        navigate(`/analyze?session=${sessionId}&skipinit=1`);
    };
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        const url = new URL(window.location);
        if (tab === 'overview') {
            url.searchParams.delete('tab');
        } else {
            url.searchParams.set('tab', tab);
        }
        window.history.pushState({}, '', url);

        // No need to load data on tab change - all data is pre-loaded
    };

    const getCreditWarningLevel = () => {
        const currentUser = dashboardData || authUser;
        if (!currentUser) return 'normal';

        // 🔧 FIX: Use architecture-based thresholds from pricing config
        // This aligns with the actual credit system where users think in terms of architectures
        const CREDITS_PER_ARCHITECTURE = pricingConfig?.workflow?.creditsPerArchitecture || 8;
        const credits = currentUser.credits || 0;

        // Critical: Can't complete even 1 more architecture
        if (credits < CREDITS_PER_ARCHITECTURE) return 'critical';

        // Warning: Can only complete 1 more architecture
        if (credits < CREDITS_PER_ARCHITECTURE * 2) return 'warning';

        // Normal: Can complete 2+ architectures
        return 'normal';
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'complete': return <Icon name="check" size="xs" className="text-green-400" />;
            case 'in-progress': return <Icon name="clock" size="xs" className="text-yellow-400" />;
            default: return <Icon name="clipboard" size="xs" className="text-gray-400" />;
        }
    };

    // Loading state
    if (authLoading || (loading && !dashboardData)) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="lg" className="mb-4" />
                    <Text variant="secondary">Loading your dashboard...</Text>
                </div>
            </div>
        );
    }

    // Redirect guest users - Dashboard requires authentication
    if (!authUser || authType === 'guest') {
        return (
            <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
                <Card className="text-center max-w-md p-8">
                    <Icon name="lock" size="lg" className="mb-4 mx-auto text-[#7B61FF]" />
                    <Heading level={2} className="mb-2">Authentication Required</Heading>
                    <Text variant="secondary" className="mb-4">
                        {authType === 'guest'
                            ? 'Please sign in with Google to access your dashboard and credit analytics.'
                            : 'Please sign in to access your dashboard.'
                        }
                    </Text>
                    <div className="space-y-3">
                        <Button
                            onClick={() => navigate('/signin')}
                            variant="primary"
                            className="w-full"
                        >
                            Sign In with Google
                        </Button>
                        <Button
                            onClick={() => navigate('/')}
                            variant="secondary"
                            className="w-full"
                        >
                            Back to Home
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }

    const user = dashboardData || authUser;

    return (
        <div className="min-h-screen bg-black text-white selection:bg-[#7B61FF] selection:text-white overflow-x-hidden relative">
            {/* Aurora Background */}
            <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>

            {/* Ambient Orbs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#7B61FF]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#9F7AEA]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Navigation */}
            {/* Desktop Navigation */}
            <Navigation />

            {/* Dashboard Sub-Header (Sticky below Nav) */}
            <header className="hidden md:block sticky top-[60px] md:top-[73px] z-40 bg-black/50 backdrop-blur-xl border-b border-white/10 transition-all duration-300">
                <Container size="7xl">
                    {/* Desktop-only Dashboard Actions */}
                    <div className="hidden md:flex justify-end items-center py-2 relative z-50">
                        <div className="flex items-center space-x-4">
                            {/* Desktop - Credit Balance */}
                            <Badge
                                variant={getCreditWarningLevel() === 'critical' ? 'error' :
                                    getCreditWarningLevel() === 'warning' ? 'warning' : 'secondary'}
                            >
                                <Icon name="creditCard" size="sm" className="mr-2" />
                                <span>{(user.credits || 0).toLocaleString()} Credits</span>
                            </Badge>

                            {/* Desktop - Upgrade Button */}
                            <Button
                                onClick={handleUpgrade}
                                variant="secondary"
                                size="sm"
                            >
                                Upgrade Plan
                            </Button>
                        </div>
                    </div>
                </Container>
                {/* Mobile spacer removed */}
            </header>

            {/* Main Dashboard Content */}
            <main className="pb-20 md:pb-6 relative z-10">
                <Container size="7xl" className="pt-4 md:py-6 md:pt-2">
                    {/* Mobile Tab Header */}
                    <div className="md:hidden mb-4">
                        <Heading level={2} className="text-xl">
                            {activeTab === 'overview' && 'Dashboard'}
                            {activeTab === 'billing' && 'Billing'}
                            {activeTab === 'usage' && 'Usage Analytics'}
                            {activeTab === 'architectures' && 'My Architectures'}
                            {activeTab === 'apps' && 'My Apps'}
                            {activeTab === 'referrals' && 'Referrals'}
                            {activeTab === 'security' && 'Security'}
                        </Heading>
                    </div>

                    {/* Tab Navigation */}
                    {/* Responsive Tabs - Scrollable on mobile, Centered on desktop */}
                    <div className="hidden md:flex overflow-x-auto pb-4 mb-8 md:justify-center md:pb-0 relative z-10 hide-scrollbar px-4 md:px-0">
                        <div className="inline-flex items-center bg-white/5 backdrop-blur-md rounded-full p-1 border border-white/10">
                            <button
                                onClick={() => handleTabChange('overview')}
                                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${activeTab === 'overview'
                                    ? 'bg-[#7B61FF] text-white shadow-[0_0_20px_rgba(123,97,255,0.3)]'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                <Icon name="home" size="xs" className="mr-2" />
                                Dashboard
                            </button>
                            <button
                                onClick={() => handleTabChange('billing')}
                                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${activeTab === 'billing'
                                    ? 'bg-[#7B61FF] text-white shadow-[0_0_20px_rgba(123,97,255,0.3)]'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                <Icon name="creditCard" size="xs" className="mr-2" />
                                Billing
                            </button>
                            <button
                                onClick={() => handleTabChange('usage')}
                                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${activeTab === 'usage'
                                    ? 'bg-[#7B61FF] text-white shadow-[0_0_20px_rgba(123,97,255,0.3)]'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                <Icon name="chart" size="xs" className="mr-2" />
                                Usage
                            </button>
                            <button
                                onClick={() => handleTabChange('architectures')}
                                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${activeTab === 'architectures'
                                    ? 'bg-[#7B61FF] text-white shadow-[0_0_20px_rgba(123,97,255,0.3)]'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                <Icon name="layers" size="xs" className="mr-2" />
                                My Architectures
                            </button>
                            <button
                                onClick={() => handleTabChange('apps')}
                                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${activeTab === 'apps'
                                    ? 'bg-[#F6821F] text-white shadow-[0_0_20px_rgba(246,130,31,0.3)]'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                <Icon name="rocket" size="xs" className="mr-2" />
                                My Apps
                            </button>
                            <button
                                onClick={() => handleTabChange('referrals')}
                                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${activeTab === 'referrals'
                                    ? 'bg-[#7B61FF] text-white shadow-[0_0_20px_rgba(123,97,255,0.3)]'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                <Icon name="gift" size="xs" className="mr-2" />
                                Referrals
                            </button>
                            <button
                                onClick={() => handleTabChange('security')}
                                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 flex items-center ${activeTab === 'security'
                                    ? 'bg-[#7B61FF] text-white shadow-[0_0_20px_rgba(123,97,255,0.3)]'
                                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                                    }`}
                            >
                                <Icon name="shield" size="xs" className="mr-2" />
                                Security
                            </button>
                        </div>
                    </div>

                    {/* Error Banner */}
                    {error && user && (
                        <Alert variant="warning" className="mb-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Text weight="medium" className="text-[#FFB800]">Some data may be outdated</Text>
                                    <Text size="sm" className="text-[#FFB800]/80">{error}</Text>
                                </div>
                                <Button
                                    onClick={refreshData}
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#FFB800] hover:text-[#FFB800]/80"
                                >
                                    Refresh
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* Tab Content */}
                    {activeTab === 'overview' && (
                        <>
                            {/* Unified Dashboard Grid - Responsive */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                {/* Left Column - Main Dashboard */}
                                <div className="lg:col-span-2 space-y-6">
                                    {/* Credit Balance Section */}
                                    <Card className="p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <Heading level={2}>Credit Balance</Heading>
                                            {getCreditWarningLevel() !== 'normal' && (
                                                <Badge
                                                    variant={getCreditWarningLevel() === 'critical' ? 'error' : 'warning'}
                                                >
                                                    {getCreditWarningLevel() === 'critical' ? 'Low Credits' : 'Warning'}
                                                </Badge>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                                            <MetricCard
                                                value={(user.credits || 0).toLocaleString()}
                                                label="Available Credits"
                                                accent="purple"
                                            />

                                            <MetricCard
                                                value={(billingData?.overview?.credits?.subscription || 0).toLocaleString()}
                                                label="Plan Credits"
                                                subtext="Expire on cancel"
                                                accent="blue"
                                            />

                                            <MetricCard
                                                value={(billingData?.overview?.credits?.purchased || 0).toLocaleString()}
                                                label="Bonus Credits"
                                                subtext="Keep forever"
                                                accent="green"
                                            />

                                            <MetricCard
                                                value={`-${user.creditsUsedToday}`}
                                                label="Used Today"
                                                accent="purple"
                                            />

                                            <MetricCard
                                                value={user.nextRefill ? formatUsageTime(user.nextRefill) : 'N/A'}
                                                label="Next Refill"
                                                accent="purple"
                                            />
                                        </div>

                                        {getCreditWarningLevel() !== 'normal' && (
                                            <Alert
                                                variant={getCreditWarningLevel() === 'critical' ? 'error' : 'warning'}
                                                className="mt-4"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <Text weight="medium" className="flex items-center" as="span">
                                                            {getCreditWarningLevel() === 'critical' ?
                                                                <>
                                                                    <Icon name="warning" size="xs" className="mr-1" />
                                                                    Low Credits Alert
                                                                </> :
                                                                <>
                                                                    <Icon name="lightbulb" size="xs" className="mr-1" />
                                                                    Credit Warning
                                                                </>
                                                            }
                                                        </Text>
                                                        <Text size="sm">
                                                            You have {user.credits} credits left ({Math.floor((user.credits || 0) / (pricingConfig?.workflow?.creditsPerArchitecture || 8))} architectures remaining)
                                                        </Text>
                                                    </div>
                                                    <div className="flex space-x-2">
                                                        <Button
                                                            onClick={handleUpgrade}
                                                            variant="secondary"
                                                            size="sm"
                                                        >
                                                            Upgrade Plan
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Alert>
                                        )}
                                    </Card>

                                    {/* Latest Saved Session */}
                                    {latestSession && (
                                        <Card className="p-6">
                                            <Heading level={2} className="mb-4">Latest Saved Session</Heading>
                                            <div className="flex flex-col md:flex-row items-start justify-between gap-4">
                                                <div className="flex-1 w-full min-w-0">
                                                    <Heading level={3} className="mb-2 flex items-center break-words">
                                                        <Icon name="target" size="xs" className="mr-2 flex-shrink-0" />
                                                        <span className="break-words line-clamp-2">
                                                            {latestSession.title || `${latestSession.idea} (${latestSession.platform})`}
                                                        </span>
                                                    </Heading>
                                                    {latestSession.description && (
                                                        <Text variant="secondary" className="mb-3 break-words line-clamp-2">{latestSession.description}</Text>
                                                    )}
                                                    <div className="flex flex-wrap items-center gap-3 mb-4">
                                                        <Badge
                                                            variant={latestSession.completionPercentage === 100 ? 'success' : 'warning'}
                                                            className="flex items-center"
                                                        >
                                                            {latestSession.completionPercentage === 100 ? (
                                                                <>
                                                                    <Icon name="check" size="xs" className="mr-1" />
                                                                    Complete
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Icon name="clock" size="xs" className="mr-1" />
                                                                    {latestSession.completionPercentage}% Complete
                                                                </>
                                                            )}
                                                        </Badge>
                                                        <Text size="sm" variant="secondary" className="flex items-center whitespace-nowrap" as="span">
                                                            <Icon name="lightbulb" size="xs" className="mr-1" />
                                                            {latestSession.platform}
                                                        </Text>
                                                        <Text size="sm" variant="secondary" className="flex items-center whitespace-nowrap" as="span">
                                                            <Icon name="calendar" size="xs" className="mr-1" />
                                                            {formatUsageTime(latestSession.updated_at)}
                                                        </Text>
                                                    </div>

                                                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <Text size="sm" variant="secondary" className="flex items-center mr-2" as="span">
                                                                <Icon name="clipboard" size="xs" className="mr-1" />
                                                                Progress:
                                                            </Text>
                                                            <Text size="sm" className="flex items-center whitespace-nowrap" as="span">
                                                                {latestSession.research ? <Icon name="check" size="xs" className="text-green-400 mr-1" /> : <Icon name="x" size="xs" className="text-red-400 mr-1" />} Research
                                                            </Text>
                                                            <Text size="sm" className="flex items-center whitespace-nowrap" as="span">
                                                                {latestSession.validation ? <Icon name="check" size="xs" className="text-green-400 mr-1" /> : <Icon name="x" size="xs" className="text-red-400 mr-1" />} Validation
                                                            </Text>
                                                            <Text size="sm" className="flex items-center whitespace-nowrap" as="span">
                                                                {latestSession.analysis ? <Icon name="check" size="xs" className="text-green-400 mr-1" /> : <Icon name="x" size="xs" className="text-red-400 mr-1" />} Analysis
                                                            </Text>
                                                            <Text size="sm" className="flex items-center whitespace-nowrap" as="span">
                                                                {latestSession.prompts ? <Icon name="check" size="xs" className="text-green-400 mr-1" /> : <Icon name="x" size="xs" className="text-red-400 mr-1" />} Prompts
                                                            </Text>
                                                            <Text size="sm" className="flex items-center whitespace-nowrap" as="span">
                                                                {latestSession.flow_diagram ? <Icon name="check" size="xs" className="text-green-400 mr-1" /> : <Icon name="x" size="xs" className="text-red-400 mr-1" />} Flow
                                                            </Text>
                                                        </div>
                                                    </div>
                                                </div>

                                                <Button
                                                    onClick={() => handleResumeSession(latestSession.sessionId || latestSession.id)}
                                                    variant="primary"
                                                    className="w-full md:w-auto mt-4 md:mt-0"
                                                >
                                                    {latestSession.completionPercentage === 100 ? 'View Architecture' : 'Continue Session'}
                                                </Button>
                                            </div>
                                        </Card>
                                    )}

                                    {/* Quick Actions Panel */}
                                    <Card className="p-6">
                                        <Heading level={2} className="mb-4">Quick Actions</Heading>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <button
                                                onClick={handleNewProject}
                                                className="glass-panel p-4 flex flex-col items-center rounded-xl hover:bg-white/10 hover:border-white/20 transition-all duration-300 group hover:shadow-lg hover:shadow-[#7B61FF]/10 text-center"
                                            >
                                                <Icon name="plus" size="sm" className="mb-2 group-hover:scale-110 transition-transform text-[#7B61FF]" />
                                                <Text size="sm" weight="medium" className="group-hover:text-white transition-colors">New Project</Text>
                                            </button>

                                            {/* Removed Buy Credits quick action */}

                                            <button className="glass-panel p-4 flex flex-col items-center rounded-xl hover:bg-white/10 hover:border-white/20 transition-all duration-300 group hover:shadow-lg hover:shadow-[#7B61FF]/10 text-center">
                                                <Icon name="refresh" size="sm" className="mb-2 group-hover:scale-110 transition-transform text-[#7B61FF]" />
                                                <Text size="sm" weight="medium" className="group-hover:text-white transition-colors">Resume Last</Text>
                                            </button>
                                        </div>
                                    </Card>

                                    {/* Recent Sessions */}
                                    <Card className="p-6">
                                        <Heading level={2} className="mb-4">Recent Sessions</Heading>
                                        {recentSessions.length > 0 ? (
                                            <div className="space-y-4">
                                                {recentSessions.map((session) => (
                                                    <div key={session.sessionId || session.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl hover:bg-white/5 border border-transparent hover:border-white/5 transition-all duration-200 gap-4 sm:gap-0">
                                                        <div className="flex-1">
                                                            <div className="flex flex-col">
                                                                {(() => {
                                                                    // Determine if title serves as the main idea/description
                                                                    const isLongTitle = session.title && session.title.length > 60;
                                                                    const isDuplicate = session.title === session.idea;
                                                                    const showTitleSeparately = session.title && !isLongTitle && !isDuplicate;

                                                                    return (
                                                                        <>
                                                                            {showTitleSeparately && (
                                                                                <Text weight="medium" className="flex items-center" as="span">
                                                                                    <Icon name="phone" size="xs" className="mr-2 shrink-0" />
                                                                                    {session.title}
                                                                                </Text>
                                                                            )}
                                                                            <TruncatedSessionIdea
                                                                                idea={showTitleSeparately ? session.idea : (session.title || session.idea)}
                                                                                className={!showTitleSeparately ? "font-medium text-white text-base flex items-start" : "text-sm text-gray-400 font-normal"}
                                                                                icon={!showTitleSeparately ? "phone" : null}
                                                                            />
                                                                        </>
                                                                    );
                                                                })()}
                                                            </div>
                                                            <div className="flex items-center space-x-4 mt-1">
                                                                <Text size="sm" variant="secondary" className="flex items-center" as="span">
                                                                    <Icon name="calendar" size="xs" className="mr-1" />
                                                                    {formatUsageTime(session.updated_at)}
                                                                </Text>
                                                                <Badge
                                                                    variant={session.completionPercentage === 100 ? 'success' : 'warning'}
                                                                    size="sm"
                                                                >
                                                                    {session.completionPercentage}% Complete
                                                                </Badge>
                                                                <Text size="sm" variant="secondary" className="flex items-center" as="span">
                                                                    <Icon name="lightbulb" size="xs" className="mr-1" />
                                                                    {session.platform}
                                                                </Text>
                                                            </div>
                                                        </div>

                                                        <Button
                                                            onClick={() => handleResumeSession(session.sessionId || session.id)}
                                                            variant="ghost"
                                                            size="sm"
                                                        >
                                                            {session.completionPercentage === 100 ? 'View' : 'Continue'}
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-8">
                                                <Icon name="clipboard" size="lg" className="mb-2 mx-auto text-[#7B61FF]" />
                                                <Text variant="secondary">No saved sessions yet</Text>
                                                <Text size="sm" variant="muted">Start analyzing ideas to see your sessions here</Text>
                                            </div>
                                        )}
                                    </Card>
                                </div>

                                {/* Right Column - Plan & Analytics */}
                                <div className="space-y-6">
                                    {/* Plan & Usage Overview */}
                                    <Card className="p-6">
                                        <Heading level={2} className="mb-4 capitalize">{user.plan}</Heading>

                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center">
                                                <Text size="sm" variant="secondary" className="flex items-center" as="span">
                                                    <Icon name="calendar" size="xs" className="mr-1" />
                                                    Billing
                                                </Text>
                                                <Text weight="medium">${user.billingAmount}/month</Text>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <Text size="sm" variant="secondary" className="flex items-center" as="span">
                                                        <Icon name="chart" size="xs" className="mr-1" />
                                                        This Month
                                                    </Text>
                                                    <Text weight="medium">{user.creditsUsedThisMonth.toLocaleString()}/{(user.totalCreditsThisMonth || user.monthlyLimit || pricingConfig?.plans?.find(p => p.id === (user?.plan || 'free'))?.credits || 10).toLocaleString()} credits</Text>
                                                </div>
                                                <div className="w-full bg-white/10 rounded-full h-2">
                                                    <div
                                                        className="bg-[#7B61FF] h-2 rounded-full transition-all shadow-[0_0_10px_rgba(123,97,255,0.3)]"
                                                        style={{ width: `${(user.creditsUsedThisMonth / (user.totalCreditsThisMonth || user.monthlyLimit || pricingConfig?.plans?.find(p => p.id === (user?.plan || 'free'))?.credits || 10)) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-center">
                                            <Text size="sm" variant="secondary" className="flex items-center" as="span">
                                                <Icon name="rocket" size="xs" className="mr-1" />
                                                Next Reset
                                            </Text>
                                            <Text weight="medium">{user.nextReset} days</Text>
                                        </div>

                                        <Button
                                            onClick={handleUpgrade}
                                            variant="primary"
                                            className="w-full flex items-center justify-center"
                                        >
                                            <Icon name="arrowUp" size="xs" className="mr-2" />
                                            Upgrade Plan
                                        </Button>
                                    </Card>

                                    {/* Session Cost Preview - Real Data */}
                                    <Card className="p-6">
                                        <Heading level={2} className="mb-4 flex items-center">
                                            <Icon name="money" size="xs" className="mr-2" />
                                            Session Cost Estimate
                                        </Heading>
                                        <Text size="xs" variant="secondary" className="mb-4">Based on your transaction history</Text>

                                        <div className="space-y-3">
                                            {(() => {
                                                const stepDisplayNames = {
                                                    'research': (
                                                        <span className="flex items-center">
                                                            <Icon name="search" size="xs" className="mr-1.5" />
                                                            Research
                                                        </span>
                                                    ),
                                                    'validation': (
                                                        <span className="flex items-center">
                                                            <Icon name="check" size="xs" className="mr-1.5" />
                                                            Validation
                                                        </span>
                                                    ),
                                                    'analysis_openai': (
                                                        <span className="flex items-center">
                                                            <Icon name="brain" size="xs" className="mr-1.5" />
                                                            Analysis (OpenAI)
                                                        </span>
                                                    ),
                                                    'analysis_claude': (
                                                        <span className="flex items-center">
                                                            <Icon name="brain" size="xs" className="mr-1.5" />
                                                            Analysis (Claude)
                                                        </span>
                                                    ),
                                                    'analysis': (
                                                        <span className="flex items-center">
                                                            <Icon name="chart" size="xs" className="mr-1.5" />
                                                            Analysis
                                                        </span>
                                                    ),
                                                    'prompts': (
                                                        <span className="flex items-center">
                                                            <Icon name="lightbulb" size="xs" className="mr-1.5" />
                                                            Prompts
                                                        </span>
                                                    ),
                                                    'flow_diagram': (
                                                        <span className="flex items-center">
                                                            <Icon name="clipboard" size="xs" className="mr-1.5" />
                                                            Flow Diagram
                                                        </span>
                                                    )
                                                };

                                                const stepOrder = ['research', 'validation', 'analysis', 'prompts', 'flow_diagram'];
                                                const costs = user.stepCostAverages || {};

                                                // Calculate total
                                                let totalCost = 0;
                                                const availableSteps = stepOrder.filter(step => typeof costs[step] === 'number' && costs[step] > 0);

                                                if (availableSteps.length === 0) {
                                                    // Use pricing from API (single source of truth)
                                                    const stepCosts = pricingConfig?.workflow?.creditsByStep || {
                                                        research: 2.0,
                                                        validation: 1.0,
                                                        analysis: 1.5,
                                                        prompts: 3.0,
                                                        diagram: 0.5
                                                    };
                                                    const totalCredits = pricingConfig?.workflow?.creditsPerArchitecture || 8;

                                                    return (
                                                        <>
                                                            <div className="flex justify-between items-center py-2 border-b border-white/5">
                                                                <Text size="sm" variant="secondary" className="flex items-center" as="span">
                                                                    <Icon name="search" size="xs" className="mr-1.5" />
                                                                    Research
                                                                </Text>
                                                                <Text weight="medium">~{stepCosts.research} credits</Text>
                                                            </div>
                                                            <div className="flex justify-between items-center py-2 border-b border-white/5">
                                                                <Text size="sm" variant="secondary" className="flex items-center" as="span">
                                                                    <Icon name="check" size="xs" className="mr-1.5" />
                                                                    Validation
                                                                </Text>
                                                                <Text weight="medium">~{stepCosts.validation} credits</Text>
                                                            </div>
                                                            <div className="flex justify-between items-center py-2 border-b border-white/5">
                                                                <Text size="sm" variant="secondary" className="flex items-center" as="span">
                                                                    <Icon name="brain" size="xs" className="mr-1.5" />
                                                                    Analysis
                                                                </Text>
                                                                <Text weight="medium">~{stepCosts.analysis} credits</Text>
                                                            </div>
                                                            <div className="flex justify-between items-center py-2 border-b border-white/5">
                                                                <Text size="sm" variant="secondary" className="flex items-center" as="span">
                                                                    <Icon name="lightbulb" size="xs" className="mr-1.5" />
                                                                    Prompts
                                                                </Text>
                                                                <Text weight="medium">~{stepCosts.prompts} credits</Text>
                                                            </div>
                                                            <div className="flex justify-between items-center py-2 border-b border-white/5">
                                                                <Text size="sm" variant="secondary" className="flex items-center" as="span">
                                                                    <Icon name="clipboard" size="xs" className="mr-1.5" />
                                                                    Flow Diagram
                                                                </Text>
                                                                <Text weight="medium">~{stepCosts.diagram} credits</Text>
                                                            </div>
                                                            <div className="flex justify-between items-center py-2 pt-3 border-t-2 border-white/10">
                                                                <Text weight="medium">Average Total</Text>
                                                                <Text weight="bold" size="lg">~{totalCredits} credits</Text>
                                                            </div>
                                                            <div className="text-center">
                                                                <Text size="xs" className="text-[#7B61FF]">Standard pricing per architecture</Text>
                                                            </div>
                                                        </>
                                                    );
                                                }

                                                return (
                                                    <>
                                                        {availableSteps.map(step => {
                                                            // Use historical average for all steps except prompts,
                                                            // which should use pricing config (not per-batch average)
                                                            let cost = Math.round(costs[step] * 100) / 100; // Round to 2 decimals
                                                            if (step === 'prompts') {
                                                                cost = pricingConfig?.workflow?.creditsByStep?.prompts || 3.0;
                                                            }
                                                            totalCost += cost;
                                                            return (
                                                                <div key={step} className="flex justify-between items-center py-2 border-b border-white/5">
                                                                    <Text size="sm" variant="secondary">{stepDisplayNames[step]}</Text>
                                                                    <Text weight="medium">{cost.toLocaleString()} credits</Text>
                                                                </div>
                                                            );
                                                        })}
                                                        <div className="flex justify-between items-center py-2 pt-3 border-t-2 border-white/10">
                                                            <Text weight="medium">Average Total</Text>
                                                            <Text weight="bold" size="lg">{Math.round(totalCost).toLocaleString()} credits</Text>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>

                                    </Card>

                                </div>

                            </div>
                        </>
                    )}

                    {/* Billing Tab */}
                    {activeTab === 'billing' && (
                        <BillingTab
                            user={user}
                            billingData={billingData}
                            loading={loading}
                            onRefresh={refreshData}
                        />
                    )}

                    {activeTab === 'usage' && (
                        <UsageTab
                            user={user}
                            preloadedData={billingData}
                            onRefresh={refreshData}
                            pricingConfig={pricingConfig}
                        />
                    )}

                    {(() => {
                        return null;
                    })()}
                    {activeTab === 'architectures' && (
                        <Card className="p-4 md:p-6">
                            <Heading level={2} className="mb-4">My Architectures</Heading>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                                <div className="lg:col-span-2">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                                        <input
                                            type="text"
                                            placeholder="Search by idea, title, or platform..."
                                            className="px-4 py-3 rounded-xl bg-black/20 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#7B61FF]/50 focus:bg-black/40 transition-all w-full sm:max-w-md"
                                            onChange={(e) => setArchitectureQuery?.(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        {/* Filter out cloudflare sessions - those go to My Apps */}
                                        {(allSessions || []).filter(s => s.platform !== 'cloudflare').map((session) => (
                                            <div key={session.sessionId || session.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 hover:bg-white/10 transition-all duration-200">
                                                <div className="flex-1">
                                                    <Text weight="medium" className="flex items-center mb-2" as="span">
                                                        <Icon name="layers" size="xs" className="mr-2" />
                                                        {session.title || `${session.idea} (${session.platform})`}
                                                    </Text>
                                                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                                        <Badge variant={session.completionPercentage === 100 ? 'success' : 'warning'} size="xs">
                                                            {session.completionPercentage}% Complete
                                                        </Badge>
                                                        <Text size="xs" variant="secondary" className="flex items-center" as="span">
                                                            <Icon name="calendar" size="xs" className="mr-1" />
                                                            {formatUsageTime(session.updated_at)}
                                                        </Text>
                                                        <Text size="xs" variant="secondary" className="flex items-center" as="span">
                                                            <Icon name="lightbulb" size="xs" className="mr-1" />
                                                            {session.platform}
                                                        </Text>
                                                    </div>
                                                </div>
                                                <Button onClick={() => handleResumeSession(session.sessionId || session.id)} variant="ghost" size="sm" className="self-start sm:self-auto">
                                                    {session.completionPercentage === 100 ? 'View' : 'Continue'}
                                                </Button>
                                            </div>
                                        ))}
                                        {(allSessions || []).filter(s => s.platform !== 'cloudflare').length === 0 && (
                                            <div className="text-center py-6">
                                                <Text variant="secondary">No architectures yet</Text>
                                                <Text size="sm" variant="muted">Start a new analysis to see it here</Text>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    <Card className="p-4">
                                        <Heading level={3} className="mb-2">Tips</Heading>
                                        <Text variant="secondary" size="sm">Use search to quickly find architectures. Click Continue to resume where you left off.</Text>
                                    </Card>
                                </div>
                            </div>
                        </Card>
                    )}

                    {activeTab === 'apps' && (
                        <Card className="p-4 md:p-6 border-[#F6821F]/20">
                            <div className="flex items-center gap-3 mb-4">
                                <img src="https://www.cloudflare.com/favicon.ico" alt="Cloudflare" className="w-6 h-6" />
                                <Heading level={2}>My Apps</Heading>
                                <Badge variant="warning" size="xs" className="bg-[#F6821F]/20 text-[#F6821F] border-[#F6821F]/30">Cloudflare</Badge>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                                <div className="lg:col-span-2">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                                        <input
                                            type="text"
                                            placeholder="Search apps..."
                                            className="px-4 py-3 rounded-xl bg-black/20 border border-[#F6821F]/20 text-white placeholder-gray-500 focus:outline-none focus:border-[#F6821F]/50 focus:bg-black/40 transition-all w-full sm:max-w-md"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        {/* Show cloudflare and vibe sessions */}
                                        {(allSessions || []).filter(s => s.platform === 'cloudflare' || s.platform === 'vibe').map((session) => (
                                            <div key={session.sessionId || session.id} className="flex flex-col gap-3 p-4 rounded-xl bg-[#F6821F]/5 border border-[#F6821F]/20 hover:border-[#F6821F]/40 hover:bg-[#F6821F]/10 transition-all duration-200">
                                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                    <div className="flex-1">
                                                        <Text weight="medium" className="flex items-center mb-2" as="span">
                                                            <Icon name="rocket" size="xs" className="mr-2 text-[#F6821F]" />
                                                            {session.title || session.idea}
                                                        </Text>
                                                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                                            <Badge variant={session.completionPercentage === 100 ? 'success' : 'warning'} size="xs">
                                                                {session.completionPercentage}% Complete
                                                            </Badge>
                                                            <Text size="xs" variant="secondary" className="flex items-center" as="span">
                                                                <Icon name="calendar" size="xs" className="mr-1" />
                                                                {formatUsageTime(session.updated_at)}
                                                            </Text>
                                                            <Text size="xs" className={`flex items-center ${session.platform === 'vibe' ? 'text-[#06B6D4]' : 'text-[#F6821F]'}`} as="span">
                                                                {session.platform === 'vibe' ? (
                                                                    <Icon name="code" size="xs" className="mr-1" />
                                                                ) : (
                                                                    <img src="https://www.cloudflare.com/favicon.ico" alt="" className="w-3 h-3 mr-1" />
                                                                )}
                                                                {session.platform === 'vibe' ? 'Vibe' : 'Cloudflare'}
                                                            </Text>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        onClick={() => session.platform === 'vibe'
                                                            ? navigate(`/vibe/${session.sessionId || session.id}`)
                                                            : handleResumeSession(session.sessionId || session.id)
                                                        }
                                                        variant="ghost"
                                                        size="sm"
                                                        className={`self-start sm:self-auto ${session.platform === 'vibe' ? 'text-[#06B6D4] hover:bg-[#06B6D4]/20' : 'text-[#F6821F] hover:bg-[#F6821F]/20'}`}
                                                    >
                                                        {session.platform === 'vibe'
                                                            ? (session.hasDeployment ? 'Open Vibe' : 'Continue Vibe')
                                                            : (session.completionPercentage === 100 ? 'View Architecture' : 'Continue')
                                                        }
                                                    </Button>
                                                </div>
                                                {/* Deployment Status Section */}
                                                {session.hasDeployment && (
                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-[#F6821F]/20">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            {session.isPaused && (
                                                                <Badge variant="warning" size="xs" className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                                                                    <Icon name="pause" size="xs" className="mr-1" />
                                                                    Paused at {session.deployment?.pausedPhase || session.deployment?.phase}
                                                                </Badge>
                                                            )}
                                                            {session.isDeploying && (
                                                                <Badge variant="info" size="xs" className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 animate-pulse">
                                                                    <Icon name="loader" size="xs" className="mr-1 animate-spin" />
                                                                    {session.deployment?.status === 'generating' ? 'Generating' :
                                                                     session.deployment?.status === 'building' ? 'Building' : 'Deploying'}
                                                                </Badge>
                                                            )}
                                                            {session.isDeployed && (
                                                                <Badge variant="success" size="xs" className={session.platform === 'vibe' && session.deployment?.githubUrl ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'bg-green-500/20 text-green-400 border-green-500/30'}>
                                                                    <Icon name={session.platform === 'vibe' && session.deployment?.githubUrl ? 'brand-github' : 'check'} size="xs" className="mr-1" />
                                                                    {session.platform === 'vibe' && session.deployment?.githubUrl ? 'On GitHub' : 'Deployed'}
                                                                </Badge>
                                                            )}
                                                            {/* Vibe Ready - has preview but not pushed to GitHub yet */}
                                                            {session.platform === 'vibe' && !session.isDeployed && session.deployment?.previewUrl && (
                                                                <Badge variant="info" size="xs" className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                                                                    <Icon name="check" size="xs" className="mr-1" />
                                                                    Ready
                                                                </Badge>
                                                            )}
                                                            {session.deployment?.filesCount > 0 && (
                                                                <Text size="xs" variant="secondary" as="span">
                                                                    {session.deployment.filesCount} files
                                                                </Text>
                                                            )}
                                                            {session.deployment?.previewUrl && (
                                                                <a href={session.deployment.previewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center">
                                                                    <Icon name="externalLink" size="xs" className="mr-1" />
                                                                    Preview
                                                                </a>
                                                            )}
                                                            {session.deployment?.deployedUrl && (
                                                                <a href={session.deployment.deployedUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-green-400 hover:text-green-300 flex items-center">
                                                                    <Icon name="externalLink" size="xs" className="mr-1" />
                                                                    Live
                                                                </a>
                                                            )}
                                                            {session.deployment?.githubUrl && (
                                                                <a href={session.deployment.githubUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-400 hover:text-purple-300 flex items-center">
                                                                    <Icon name="brand-github" size="xs" className="mr-1" />
                                                                    GitHub
                                                                </a>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Button
                                                                onClick={() => navigate(session.platform === 'vibe'
                                                                    ? `/vibe/${session.sessionId || session.id}`
                                                                    : `/deploy/${session.sessionId || session.id}`
                                                                )}
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-cyan-400 hover:bg-cyan-500/20"
                                                            >
                                                                <Icon name="code" size="xs" className="mr-1" />
                                                                {session.platform === 'vibe'
                                                                    ? 'Open Vibe'
                                                                    : (session.isPaused ? 'Resume Deploy' : session.isDeployed ? 'View Deploy' : 'View Deploy')
                                                                }
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Show Deploy button if architecture is complete but no deployment (not for vibe - they deploy via vibe interface) */}
                                                {!session.hasDeployment && session.completionPercentage === 100 && session.platform !== 'vibe' && (
                                                    <div className="flex items-center justify-end pt-3 border-t border-[#F6821F]/20">
                                                        <Button
                                                            onClick={() => navigate(`/deploy/${session.sessionId || session.id}`)}
                                                            variant="outline"
                                                            size="sm"
                                                            className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
                                                        >
                                                            <Icon name="rocket" size="xs" className="mr-1" />
                                                            Deploy to Cloudflare
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {(allSessions || []).filter(s => s.platform === 'cloudflare' || s.platform === 'vibe').length === 0 && (
                                            <div className="text-center py-12 border border-dashed border-[#F6821F]/30 rounded-xl">
                                                <Icon name="rocket" size="xl" className="text-[#F6821F]/40 mb-3" />
                                                <Text variant="secondary">No apps yet</Text>
                                                <Text size="sm" variant="muted" className="mt-1">Choose "Deploy" or "Vibe Coding" on the home page to build your first app</Text>
                                                <Button
                                                    onClick={() => navigate('/')}
                                                    variant="outline"
                                                    size="sm"
                                                    className="mt-4 border-[#F6821F]/50 text-[#F6821F] hover:bg-[#F6821F]/10"
                                                >
                                                    Start Building
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    <Card className="p-4 border-[#F6821F]/20 bg-[#F6821F]/5">
                                        <div className="flex items-center gap-2 mb-2">
                                            <img src="https://www.cloudflare.com/favicon.ico" alt="" className="w-4 h-4" />
                                            <Heading level={3}>Cloudflare Apps</Heading>
                                        </div>
                                        <Text variant="secondary" size="sm">
                                            Apps built with the Deploy path are full-stack applications running on Cloudflare's global edge network.
                                        </Text>
                                        <div className="mt-3 space-y-2 text-xs text-gray-400">
                                            <div className="flex items-center gap-2">
                                                <Icon name="check" size="xs" className="text-[#F6821F]" />
                                                <span>Workers + Pages hosting</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Icon name="check" size="xs" className="text-[#F6821F]" />
                                                <span>D1 database included</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Icon name="check" size="xs" className="text-[#F6821F]" />
                                                <span>KV & R2 storage</span>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                            </div>
                        </Card>
                    )}

                    {activeTab === 'referrals' && (
                        <ReferralSection pricingConfig={pricingConfig} />
                    )}

                    {activeTab === 'security' && (
                        <SecurityTab />
                    )}
                </Container>
            </main >

            {/* Mobile Bottom Tab Navigation */}
            <MobileBottomNav items={[
                { id: 'overview', label: 'Dashboard', icon: 'home', onClick: () => handleTabChange('overview'), active: activeTab === 'overview' },
                { id: 'architectures', label: 'Projects', icon: 'layers', onClick: () => handleTabChange('architectures'), active: activeTab === 'architectures' },
                { id: 'apps', label: 'Apps', icon: 'rocket', onClick: () => handleTabChange('apps'), active: activeTab === 'apps' },
                { id: 'billing', label: 'Billing', icon: 'creditCard', onClick: () => handleTabChange('billing'), active: activeTab === 'billing' },
                { id: 'security', label: 'Security', icon: 'shield', onClick: () => handleTabChange('security'), active: activeTab === 'security' }
            ]} />
        </div>
    );
}

// NOTE: Plan credits now come from /api/v2/pricing (pricingConfig)
// Source of truth: functions/config/pricing.js

// Helper function to get recent transactions (real data only)
const getRecentTransactions = (user, billingData) => {
    const transactions = [];

    // Add other transactions from billingData if available
    if (billingData?.historyData?.transactions) {
        const apiTransactions = billingData.historyData.transactions.map(transaction => {
            // Safe date parsing with timezone conversion
            let formattedDate = 'Recent';
            const dateStr = transaction.created_at || transaction.date || transaction.createdAt;

            if (dateStr) {
                try {
                    const date = new Date(dateStr);
                    if (!isNaN(date.getTime())) {
                        formattedDate = formatUsageTime(dateStr);
                    }
                } catch (error) {
                    formattedDate = 'Recent';
                }
            }

            // Determine icon based on transaction type and operation
            const getTransactionIcon = (tx) => {
                const opType = (tx.operation_type || '').toLowerCase();
                // Subscription/purchase operations
                if (opType === 'subscription_purchase') return 'creditCard';
                if (opType === 'subscription_renewal') return 'refresh';
                if (opType === 'credit_purchase') return 'creditCard';
                if (opType === 'account_creation') return 'gift';
                if (opType.includes('referral')) return 'gift';
                // Workflow operations
                if (opType === 'research') return 'search';
                if (opType === 'validation') return 'check';
                if (opType === 'analysis' || opType.includes('analysis')) return 'brain';
                if (opType === 'prompts') return 'lightbulb';
                if (opType === 'flow_diagram' || opType === 'diagram') return 'clipboard';
                // Fallback by transaction type
                if (tx.type === 'purchase') return 'creditCard';
                if (tx.type === 'spend') return 'money';
                if (tx.type === 'credit') return 'star';
                return 'money';
            };

            // Format description for display
            const getTransactionDescription = (tx) => {
                if (tx.description) return tx.description;
                const opType = (tx.operation_type || '').toLowerCase();
                // Map operation types to friendly names
                const opTypeNames = {
                    'subscription_purchase': 'Plan Purchase',
                    'subscription_renewal': 'Plan Renewal',
                    'credit_purchase': 'Credit Purchase',
                    'account_creation': 'Welcome Credits',
                    'research': 'Research Step',
                    'validation': 'Validation Step',
                    'analysis': 'Analysis Step',
                    'analysis_claude': 'Analysis (Claude)',
                    'analysis_openai': 'Analysis (OpenAI)',
                    'prompts': 'Prompts Generation',
                    'flow_diagram': 'Flow Diagram',
                    'diagram': 'Flow Diagram'
                };
                return opTypeNames[opType] || `${tx.operation_type || 'Credit'} Transaction`;
            };

            return {
                type: transaction.type || 'transaction',
                icon: getTransactionIcon(transaction),
                description: getTransactionDescription(transaction),
                date: formattedDate,
                dateObject: dateStr ? new Date(dateStr) : new Date(), // Keep original date for sorting
                amount: transaction.amount || 0,
                status: transaction.status || 'completed'
            };
        });
        transactions.push(...apiTransactions);
    }

    // Sort by date (newest first) and limit to 5 transactions
    return transactions
        .sort((a, b) => (b.dateObject || new Date()) - (a.dateObject || new Date()))
        .slice(0, 5);
};

// Billing Tab Component - Real API data only
function BillingTab({ user, billingData, loading, onRefresh }) {
    const [downloadingId, setDownloadingId] = useState(null);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const handleCancelSubscription = async () => {
        setCancelLoading(true);
        try {
            const response = await api.request('/api/v2/billing/cancel', {
                method: 'POST'
            });

            if (response.success) {
                window.showToast?.('Subscription cancellation scheduled', 'success');
                setShowCancelConfirm(false);
                onRefresh?.(); // Refresh to show updated status
            } else {
                throw new Error(response.message || 'Failed to cancel subscription');
            }
        } catch (error) {
            log.error('Cancel error:', error);
            window.showToast?.(error.message || 'Failed to cancel subscription', 'error');
        } finally {
            setCancelLoading(false);
        }
    };

    const handleDownloadInvoice = async (transactionId) => {
        try {
            setDownloadingId(transactionId);

            // Call our API to get the actual PDF URL from Paddle
            const response = await api.request(`/api/v2/billing/invoice-download?transactionId=${transactionId}`);

            if (response.success && response.url) {
                // Open the PDF in a new tab
                window.open(response.url, '_blank');
            } else {
                throw new Error(response.message || 'Failed to get invoice download link');
            }
        } catch (error) {
            log.error('Failed to download invoice:', error);
            window.showToast?.(error.message || 'Failed to download invoice', 'error');
        } finally {
            setDownloadingId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <Spinner size="lg" className="mb-4" />
                    <Text variant="secondary">Loading billing information...</Text>
                </div>
            </div>
        );
    }

    if (!billingData) {
        return (
            <div className="text-center py-12">
                <div className="text-[#7B61FF] text-5xl mb-4">💳</div>
                <Heading level={3} className="mb-2">Unable to load billing data</Heading>
                <Text variant="secondary" className="mb-4">Please try refreshing or contact support if the problem persists.</Text>
                <Button
                    onClick={onRefresh}
                    variant="primary"
                >
                    Try Again
                </Button>
            </div>
        );
    }

    const { overview } = billingData;

    return (
        <div className="space-y-3 md:space-y-6">
            {/* Row 1: Key Metrics (3 cols on desktop) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                {/* Current Credits */}
                <MetricCard
                    value={(overview?.credits?.available || 0).toLocaleString()}
                    label="Available Credits"
                    accent="purple"
                    subtext={`of ${(overview?.credits?.current || 0).toLocaleString()} total`}
                    icon="diamond"
                />

                {/* Current Plan */}
                <MetricCard
                    value={overview?.subscription?.planName || 'Free'}
                    label="Current Plan"
                    accent="purple"
                    subtext={`${(overview?.subscription?.monthlyCredits || 0).toLocaleString()} credits/month`}
                    icon="star"
                />

                {/* This Month Spending */}
                <MetricCard
                    value={(overview?.spending?.thisMonth?.creditsUsed || 0).toLocaleString()}
                    label="Credits Used"
                    accent="purple"
                    subtext="this month"
                    icon="chart"
                />
            </div>

            {/* Row 2: Subscription & Credit Details */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-6">
                {/* Subscription Details - Takes 2 cols */}
                <div className="lg:col-span-2">
                    <Card className="p-4 md:p-6 h-full">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
                            <Heading level={3}>Subscription Details</Heading>
                            <Button
                                onClick={onRefresh}
                                variant="ghost"
                                size="sm"
                                className="self-start sm:self-auto"
                            >
                                Refresh
                            </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                            <div>
                                <Text size="sm" variant="secondary">Plan</Text>
                                <Text weight="bold" size="lg" className="capitalize">{overview?.subscription?.planName || 'Free'}</Text>
                            </div>
                            <div>
                                <Text size="sm" variant="secondary">Status</Text>
                                <Badge
                                    variant={overview?.subscription?.isActive ? 'success' : 'error'}
                                    size="sm"
                                >
                                    {overview?.subscription?.status || 'active'}
                                </Badge>
                            </div>
                            <div>
                                <Text size="sm" variant="secondary">Next Billing</Text>
                                <Text weight="bold">
                                    {overview?.subscription?.renewalDate ?
                                        formatUsageTime(overview.subscription.renewalDate) :
                                        'N/A'
                                    }
                                </Text>
                            </div>
                        </div>

                        {/* Cancel Subscription Section */}
                        {overview?.subscription?.planName &&
                            overview?.subscription?.planName.toLowerCase() !== 'free' &&
                            overview?.subscription?.status !== 'canceling' && (
                                <div className="mt-6 pt-6 border-t border-white/10">
                                    {!showCancelConfirm ? (
                                        <Button
                                            onClick={() => setShowCancelConfirm(true)}
                                            variant="ghost"
                                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                        >
                                            Cancel Subscription
                                        </Button>
                                    ) : (
                                        <div className="flex items-center gap-4 p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                                            <Text size="sm" className="text-red-300">
                                                Are you sure? Your subscription will remain active until the end of the billing period.
                                            </Text>
                                            <div className="flex gap-2">
                                                <Button
                                                    onClick={handleCancelSubscription}
                                                    disabled={cancelLoading}
                                                    variant="danger"
                                                    size="sm"
                                                >
                                                    {cancelLoading ? 'Canceling...' : 'Yes, Cancel'}
                                                </Button>
                                                <Button
                                                    onClick={() => setShowCancelConfirm(false)}
                                                    disabled={cancelLoading}
                                                    variant="ghost"
                                                    size="sm"
                                                >
                                                    Keep Subscription
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                        {/* Show cancellation notice if already canceling */}
                        {overview?.subscription?.status === 'canceling' && (
                            <div className="mt-6 pt-6 border-t border-white/10">
                                <div className="flex items-center gap-3 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                                    <Icon name="warning" size="sm" className="text-yellow-400" />
                                    <Text size="sm" className="text-yellow-300">
                                        Your subscription is set to cancel at the end of the current billing period
                                    </Text>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>

                {/* Credit Breakdown - Takes 1 col */}
                <div className="lg:col-span-1">
                    <Card className="p-4 h-full">
                        <div className="flex items-center justify-between mb-3">
                            <Heading level={3} size="sm">Credit Details</Heading>
                            <div className="p-2 bg-white/5 rounded-full">
                                <Icon name="money" size="xs" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                            <div className="p-3 bg-white/5 rounded-lg flex justify-between items-center">
                                <div>
                                    <Text size="sm" variant="secondary">Subscription</Text>
                                    <Text size="xs" variant="muted">Resets monthly</Text>
                                </div>
                                <Text weight="medium">{(overview?.credits?.subscription || 0).toLocaleString()}</Text>
                            </div>
                            <div className="p-3 bg-white/5 rounded-lg flex justify-between items-center">
                                <div>
                                    <Text size="sm" variant="secondary">Purchased</Text>
                                    <Text size="xs" variant="muted">Never expires</Text>
                                </div>
                                <Text weight="medium">{(overview?.credits?.purchased || 0).toLocaleString()}</Text>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Transactions and Invoices Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Recent Transactions */}
                <Card>
                    <div className="p-6 border-b border-white/10 flex justify-between items-center">
                        <Heading level={3}>Recent Transactions</Heading>
                        <Button
                            onClick={onRefresh}
                            variant="ghost"
                            size="sm"
                        >
                            Refresh
                        </Button>
                    </div>

                    <div className="p-6">
                        {getRecentTransactions(user, billingData).length > 0 ? (
                            <div className="space-y-3">
                                {getRecentTransactions(user, billingData).map((transaction, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 border border-white/10 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 rounded-full bg-white/5 flex items-center justify-center">
                                                <Icon name={transaction.icon} size="xs" />
                                            </div>
                                            <div>
                                                <Text size="sm" weight="medium">{transaction.description}</Text>
                                                <Text size="xs" variant="secondary">{transaction.date}</Text>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <Text size="sm" weight="medium" className={transaction.amount > 0 ? 'text-green-400' : 'text-red-400'}>
                                                {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()} credits
                                            </Text>
                                            <Text size="xs" variant="secondary" className="capitalize">{transaction.status}</Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <Icon name="chart" size="lg" className="mb-2 mx-auto text-[#7B61FF]" />
                                <Text variant="secondary">No transaction history available</Text>
                                <Text size="sm" variant="muted">Start using The Architect to see your transaction history here</Text>
                            </div>
                        )}
                    </div>
                </Card>

                {/* Recent Invoices */}
                <Card>
                    <div className="p-6 border-b border-white/10">
                        <Heading level={3}>Recent Invoices</Heading>
                    </div>

                    <div className="p-6">
                        {billingData?.invoices && billingData.invoices.length > 0 ? (
                            <div className="space-y-3">
                                {billingData.invoices.map((invoice, index) => (
                                    <div key={invoice.id || index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                                        <div className="flex items-center space-x-4">
                                            <div className="w-10 h-10 rounded-lg bg-[#7B61FF]/20 flex items-center justify-center">
                                                <Icon name="document" size="sm" className="text-[#7B61FF]" />
                                            </div>
                                            <div>
                                                <Text weight="medium">{invoice.number || invoice.id}</Text>
                                                <Text size="sm" variant="secondary">
                                                    {invoice.date ? new Date(invoice.date).toLocaleDateString() : 'N/A'}
                                                </Text>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-4">
                                            <div className="text-right">
                                                <Text weight="medium">${invoice.amount?.toFixed(2) || '0.00'}</Text>
                                                <Badge variant={invoice.status === 'paid' ? 'success' : 'warning'} size="sm">
                                                    {invoice.status || 'pending'}
                                                </Badge>
                                            </div>
                                            <button
                                                onClick={() => handleDownloadInvoice(invoice.id)}
                                                disabled={downloadingId === invoice.id}
                                                className="text-[#7B61FF] hover:text-[#6B51EF] transition-colors disabled:opacity-50 disabled:cursor-wait"
                                                title="Download Invoice PDF"
                                            >
                                                {downloadingId === invoice.id ? (
                                                    <Spinner size="sm" />
                                                ) : (
                                                    <Icon name="download" size="sm" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <Icon name="document" size="lg" className="mb-2 mx-auto text-[#7B61FF]" />
                                <Text variant="secondary">No invoices available</Text>
                                <Text size="sm" variant="muted">Invoices will appear here after your first subscription payment</Text>
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
}

// Usage Tab Component - Real API data only
function UsageTab({ user, preloadedData = null, onRefresh, pricingConfig = null }) {
    const { authType } = useAuth(); // Add auth context
    const [usageData, setUsageData] = useState(null);
    const [loading, setLoading] = useState(!preloadedData);
    const [error, setError] = useState(null);
    const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
    const [currentPage, setCurrentPage] = useState(1);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const transactionsPerPage = 20;

    // Load real usage data - use preloaded data when available
    useEffect(() => {
        // AUTHENTICATION GUARD: Only load for authenticated users
        if (!user || authType !== 'authenticated') {
            setLoading(false);
            return;
        }

        // If preloaded data exists, show it immediately for faster first paint
        if (preloadedData) {
            const transformedData = transformPreloadedData(preloadedData);
            setUsageData(transformedData);
        }
        // Always load paginated data from backend for correct totals/paging
        loadUsageData(currentPage);
    }, [selectedTimeframe, preloadedData, user, authType, currentPage]);

    // Reset pagination when timeframe filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedTimeframe]);

    const transformPreloadedData = (data) => {
        // Transform preloaded data to match expected format
        const historyData = data.historyData || { transactions: [] };
        const monthlyAnalytics = data.monthlyAnalytics || {};

        // Calculate billing period dates
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        return {
            analytics: monthlyAnalytics.analytics || { current: {} },
            rawEvents: transformHistoryToEvents(historyData.transactions || []),
            totalTransactions: historyData.pagination?.total || (historyData.transactions?.length || 0),
            currentBillingPeriod: {
                start: startOfMonth.toLocaleDateString(),
                end: endOfMonth.toLocaleDateString()
            },
            plan: (() => {
                // 🔧 FIX: Use plan with subscriptionTier fallback (both from DB via session API)
                const planId = user?.plan || user?.subscriptionTier || 'free';
                const planConfig = pricingConfig?.plans?.find(p => p.id === planId);
                return {
                    id: planId,
                    name: planConfig?.name || planId.charAt(0).toUpperCase() + planId.slice(1),
                    monthlyCredits: user?.monthlyLimit || planConfig?.credits || 10,
                    includedValue: user?.billingAmount || 0
                };
            })(),
            timeframes: [
                { label: '1 Day', value: '1d' },
                { label: '7 Days', value: '7d' },
                { label: '30 Days', value: '30d' },
                { label: 'Custom', value: 'custom' }
            ],
            autoUsageNote: 'Automated usage tracking helps optimize your account spending.'
        };
    };

    const loadUsageData = async (page = 1) => {
        // AUTHENTICATION GUARD: Don't load data for unauthenticated users
        if (!user || authType !== 'authenticated') {
            setLoading(false);
            setError('Please sign in to view usage data');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Build API options with optional date range for custom timeframe
            const analyticsOptions = {
                timeframe: getTimeframeFromSelection(selectedTimeframe),
                includeComparison: true,
                includePredictions: true,
                includeFraudCheck: false
            };

            const historyOptions = {
                limit: transactionsPerPage,
                offset: (page - 1) * transactionsPerPage
            };

            // Add custom date range if selected
            if (selectedTimeframe === 'custom' && dateRange.start && dateRange.end) {
                analyticsOptions.startDate = new Date(dateRange.start).toISOString();
                analyticsOptions.endDate = new Date(dateRange.end).toISOString();
                historyOptions.startDate = new Date(dateRange.start).toISOString();
                historyOptions.endDate = new Date(dateRange.end).toISOString();
            }

            // Get real usage analytics from the backend
            const [analyticsResponse, historyResponse] = await Promise.all([
                api.getCreditAnalytics(analyticsOptions),
                api.getCreditHistory(historyOptions)
            ]);


            // Transform real data to match the expected format
            const transformedData = {
                currentBillingPeriod: {
                    start: analyticsResponse.billingInfo?.currentPeriod?.start ?
                        new Date(analyticsResponse.billingInfo.currentPeriod.start).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                        }) : 'Current Period',
                    end: analyticsResponse.billingInfo?.currentPeriod?.end ?
                        new Date(analyticsResponse.billingInfo.currentPeriod.end).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                        }) : 'Next Period'
                },
                plan: analyticsResponse.billingInfo?.plan || (() => {
                    // Fallback: build plan from user object (already has DB data from session API)
                    const planId = user?.plan || user?.subscriptionTier || 'free';
                    const planConfig = pricingConfig?.plans?.find(p => p.id === planId);
                    return {
                        id: planId,
                        name: planConfig?.name || planId.charAt(0).toUpperCase() + planId.slice(1),
                        monthlyCredits: planConfig?.credits || 10,
                        includedValue: 0
                    };
                })(),
                proIncludedUsage: analyticsResponse.billingInfo?.plan?.includedValue || 0,
                autoUsageNote: 'Auto usage does not count towards your usage limits.',
                timeframes: [
                    { label: '1 Day', value: '1d' },
                    { label: '7 Days', value: '7d' },
                    { label: '30 Days', value: '30d' },
                    { label: 'Custom', value: 'custom' }
                ],
                rawEvents: transformHistoryToEvents(historyResponse.transactions || []),
                totalTransactions: historyResponse.pagination?.total || (historyResponse.transactions?.length || 0),
                analytics: analyticsResponse.analytics || null,
                historySummary: historyResponse.summary || null
            };

            setUsageData(transformedData);
        } catch (err) {
            setError('Failed to load usage data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Transform timeframe selection to API format
    const getTimeframeFromSelection = (selection) => {
        switch (selection) {
            case '1d': return 'daily';
            case '7d': return 'weekly';
            case '30d': return 'monthly';
            case 'custom': return 'monthly'; // Custom uses date range params instead
            default: return 'monthly';
        }
    };

    // Transform transaction history to real events format
    const transformHistoryToEvents = (transactions) => {
        if (!transactions || transactions.length === 0) {
            return [];
        }

        return transactions.map((transaction, index) => {
            // Get metadata - API already returns parsed object, not a string
            let metadata = {};
            if (transaction.metadata && typeof transaction.metadata === 'object') {
                metadata = transaction.metadata;
            } else if (transaction.metadata && typeof transaction.metadata === 'string') {
                try {
                    metadata = JSON.parse(transaction.metadata);
                } catch (e) {
                    metadata = {};
                }
            }

            // Enhanced model/provider extraction with comprehensive fallbacks
            const extractModelName = (transaction, metadata) => {
                // Non-AI transactions don't have models
                const operation = transaction.operationType || transaction.operation_type || '';
                const nonAiOperations = [
                    'subscription_purchase', 'subscription_renewal', 'credit_purchase',
                    'account_creation', 'bonus', 'refund',
                    'subscription_expired', 'referral_bonus', 'referral_welcome', 'free_monthly'
                ];
                if (nonAiOperations.includes(operation.toLowerCase())) {
                    return '—'; // Em dash for non-AI transactions
                }

                // Check direct transaction fields first (most reliable from database)
                if (transaction.model) return cleanModelName(transaction.model);
                if (transaction.modelUsed) return cleanModelName(transaction.modelUsed);
                if (transaction.model_used) return cleanModelName(transaction.model_used);

                // Check metadata fields
                if (metadata.model) return cleanModelName(metadata.model);
                if (metadata.model_used) return cleanModelName(metadata.model_used);
                if (metadata.modelName) return cleanModelName(metadata.modelName);
                if (metadata.model_name) return cleanModelName(metadata.model_name);

                // Check other transaction fields
                if (transaction.modelName) return cleanModelName(transaction.modelName);
                if (transaction.model_name) return cleanModelName(transaction.model_name);

                // Check nested data
                if (transaction.details?.model) return cleanModelName(transaction.details.model);
                if (transaction.requestDetails?.model) return cleanModelName(transaction.requestDetails.model);

                // Operation-specific model detection
                if (operation.toLowerCase().includes('claude')) return 'Claude';
                if (operation.toLowerCase().includes('gpt')) return 'GPT';
                if (operation.toLowerCase().includes('openai')) return 'OpenAI';

                return 'AI Model';
            };

            const extractProvider = (transaction, metadata) => {
                // Non-AI transactions don't have providers
                const operation = transaction.operationType || transaction.operation_type || '';
                const nonAiOperations = [
                    'subscription_purchase', 'subscription_renewal', 'credit_purchase',
                    'account_creation', 'bonus', 'refund',
                    'subscription_expired', 'referral_bonus', 'referral_welcome', 'free_monthly'
                ];
                if (nonAiOperations.includes(operation.toLowerCase())) {
                    return '—'; // Em dash for non-AI transactions
                }

                // Prefer explicit provider fields
                if (transaction.provider) return transaction.provider;
                if (metadata.provider) return metadata.provider;
                if (transaction.performance?.provider) return transaction.performance.provider;
                // Infer from model name
                const model = extractModelName(transaction, metadata).toLowerCase();
                if (model.includes('claude')) return 'Anthropic';
                if (model.includes('gpt') || model.includes('openai')) return 'OpenAI';
                if (model.includes('gemini')) return 'Google';
                return 'AI Provider';
            };

            // Clean and format model names for display
            const cleanModelName = (modelName) => {
                if (!modelName || typeof modelName !== 'string') return 'AI Model';

                // Common model name mappings
                const modelMappings = {
                    'gpt-4o': 'GPT-4o',
                    'gpt-4': 'GPT-4',
                    'gpt-3.5-turbo': 'GPT-3.5',
                    'claude-3-5-sonnet-20241022': 'Claude 3.5 Sonnet',
                    'claude-3-5-sonnet': 'Claude 3.5 Sonnet',
                    'claude-3-sonnet': 'Claude 3 Sonnet',
                    'claude-3-haiku': 'Claude 3 Haiku',
                    'claude-3-opus': 'Claude 3 Opus'
                };

                // Check for exact matches first
                if (modelMappings[modelName.toLowerCase()]) {
                    return modelMappings[modelName.toLowerCase()];
                }

                // Clean up common patterns
                let cleaned = modelName
                    .replace(/-\d{8}$/, '') // Remove date suffixes
                    .replace(/_/g, ' ')     // Replace underscores with spaces
                    .replace(/-/g, ' ')     // Replace hyphens with spaces
                    .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letters

                return cleaned || 'AI Model';
            };

            // Use real data from transaction and metadata
            const realTokens = metadata.totalTokens || metadata.total_tokens || 0;
            const realModel = extractModelName(transaction, metadata);
            const operationType = transaction.operationType || transaction.operation_type || 'operation';


            return {
                date: (() => {
                    // Try multiple date fields
                    const dateStr = transaction.date || transaction.createdAt || transaction.created_at;
                    if (dateStr && !isNaN(new Date(dateStr))) {
                        return formatUsageTime(dateStr);
                    }
                    return 'Recent';
                })(),
                user: 'You',
                kind: getTransactionKind(transaction),
                max: metadata.success === false ? 'Error' : 'No',
                provider: extractProvider(transaction, metadata),
                model: realModel,
                tokens: realTokens > 0 ? realTokens : (transaction.type === 'spend' || transaction.type === 'usage' ? -Math.abs(transaction.amount) : transaction.amount), // Use real tokens or credit amount with proper sign
                cost: calculateTransactionCost(transaction, metadata)
            };
        });
    };

    // Helper: provider inference in table rendering
    const inferProviderFromModel = (model) => {
        if (!model || typeof model !== 'string') return 'AI Provider';
        const lower = model.toLowerCase();
        if (lower.includes('claude')) return 'Anthropic';
        if (lower.includes('gpt') || lower.includes('openai')) return 'OpenAI';
        if (lower.includes('gemini')) return 'Google';
        return 'AI Provider';
    };

    // Helper function to determine transaction kind using operationType (the actual step)
    const getTransactionKind = (transaction) => {
        const operationType = transaction.operationType || transaction.operation_type || '';

        // Map database operationType to display names
        switch (operationType.toLowerCase()) {
            case 'research':
                return (
                    <>
                        <Icon name="search" size="xs" className="inline mr-1" />
                        Research
                    </>
                );
            case 'validation':
                return (
                    <>
                        <Icon name="check" size="xs" className="inline mr-1" />
                        Validation
                    </>
                );
            case 'analysis_openai':
                return (
                    <>
                        <Icon name="brain" size="xs" className="inline mr-1" />
                        Analysis (OpenAI)
                    </>
                );
            case 'analysis_claude':
                return (
                    <>
                        <Icon name="brain" size="xs" className="inline mr-1" />
                        Analysis (Claude)
                    </>
                );
            case 'analysis':
                return (
                    <>
                        <Icon name="chart" size="xs" className="inline mr-1" />
                        Analysis
                    </>
                );
            case 'prompts':
                return (
                    <>
                        <Icon name="lightbulb" size="xs" className="inline mr-1" />
                        Prompts
                    </>
                );
            case 'flow_diagram':
                return (
                    <>
                        <Icon name="clipboard" size="xs" className="inline mr-1" />
                        Flow Diagram
                    </>
                );
            case 'account_creation':
                return (
                    <>
                        <Icon name="gift" size="xs" className="inline mr-1" />
                        Welcome Credits
                    </>
                );
            case 'subscription_purchase':
                return (
                    <>
                        <Icon name="creditCard" size="xs" className="inline mr-1" />
                        Plan Purchase
                    </>
                );
            case 'subscription_renewal':
                return (
                    <>
                        <Icon name="refresh" size="xs" className="inline mr-1" />
                        Plan Renewal
                    </>
                );
            case 'credit_purchase':
                return (
                    <>
                        <Icon name="creditCard" size="xs" className="inline mr-1" />
                        Credit Purchase
                    </>
                );
            case 'subscription_expired':
                return (
                    <>
                        <Icon name="warning" size="xs" className="inline mr-1" />
                        Credits Expired
                    </>
                );
            case 'referral_bonus':
                return (
                    <>
                        <Icon name="gift" size="xs" className="inline mr-1" />
                        Referral Bonus
                    </>
                );
            case 'referral_welcome':
                return (
                    <>
                        <Icon name="gift" size="xs" className="inline mr-1" />
                        Referral Welcome
                    </>
                );
            case 'prompts_batch':
                return (
                    <>
                        <Icon name="lightbulb" size="xs" className="inline mr-1" />
                        Prompts
                    </>
                );
            case 'diagram':
                return (
                    <>
                        <Icon name="clipboard" size="xs" className="inline mr-1" />
                        Flow Diagram
                    </>
                );
            case 'free_monthly':
                return (
                    <>
                        <Icon name="gift" size="xs" className="inline mr-1" />
                        Free Monthly Credits
                    </>
                );
            default:
                // Fallback to transaction type if operationType not found
                switch (transaction.type) {
                    case 'spend':
                    case 'usage':
                        return (
                            <>
                                <Icon name="money" size="xs" className="inline mr-1" />
                                Platform Usage
                            </>
                        );
                    case 'purchase':
                        return (
                            <>
                                <Icon name="creditCard" size="xs" className="inline mr-1" />
                                Credit Purchase
                            </>
                        );
                    case 'bonus':
                        return (
                            <>
                                <Icon name="gift" size="xs" className="inline mr-1" />
                                Bonus Credits
                            </>
                        );
                    case 'refund':
                        return (
                            <>
                                <Icon name="refresh" size="xs" className="inline mr-1" />
                                Refund
                            </>
                        );
                    default:
                        return (
                            <>
                                <Icon name="clipboard" size="xs" className="inline mr-1" />
                                Transaction
                            </>
                        );
                }
        }
    };

    // Helper function to calculate real cost display
    const calculateTransactionCost = (transaction, metadata) => {
        if (transaction.type === 'spend' || transaction.type === 'usage') {
            // Show negative for spend/usage, positive for purchases
            const amount = Math.abs(transaction.amount);
            return `-${amount} credits`;
        } else if (transaction.type === 'purchase') {
            return metadata.purchaseAmount ? `$${metadata.purchaseAmount}` : 'Purchase';
        } else {
            return 'Included';
        }
    };

    const getKindBadgeColor = (kind) => {
        switch (kind) {
            case 'Included in Pro':
                return 'bg-green-100 text-green-800';
            case 'Errored, Not Charged':
                return 'bg-red-100 text-red-800';
            case 'Purchase':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Helper to extract text content from JSX kind field
    const getKindText = (kind) => {
        if (typeof kind === 'string') return kind;
        if (React.isValidElement(kind)) {
            // Extract text from JSX elements
            const getText = (element) => {
                if (typeof element === 'string') return element;
                if (typeof element === 'number') return element.toString();
                if (React.isValidElement(element)) {
                    if (element.props && element.props.children) {
                        return React.Children.toArray(element.props.children)
                            .map(child => getText(child))
                            .join('');
                    }
                }
                return '';
            };
            return getText(kind);
        }
        return 'Transaction';
    };

    // Loading state
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading usage data...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="text-center py-12">
                <Icon name="warning" size="lg" className="mb-4 mx-auto text-[#FF6B6B]" />
                <Heading level={3} className="mb-2">Failed to load usage data</Heading>
                <Text variant="secondary" className="mb-4">{error}</Text>
                <Button
                    onClick={loadUsageData}
                    variant="primary"
                >
                    Try Again
                </Button>
            </div>
        );
    }

    // No data state
    if (!usageData || !usageData.rawEvents || usageData.rawEvents.length === 0) {
        return (
            <div className="text-center py-12">
                <Icon name="rocket" size="lg" className="mb-4 mx-auto text-[#7B61FF]" />
                <Heading level={3} className="mb-2">Ready to build your app?</Heading>
                <div className="mb-4 space-y-2">
                    <Text variant="secondary">You haven't analyzed any app ideas yet.</Text>
                    <Text variant="secondary">Start using The Architect to see your usage analytics here!</Text>
                    <Text size="sm" variant="muted" className="mt-2">
                        Your {usageData?.plan?.name || 'Free'} plan includes{' '}
                        {usageData?.plan?.monthlyCredits || 100} credits per month.
                    </Text>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                        onClick={() => window.location.href = '/analyze'}
                        variant="primary"
                    >
                        Start Analyzing Ideas
                    </Button>
                    <Button
                        onClick={onRefresh}
                        variant="secondary"
                    >
                        Refresh Data
                    </Button>
                </div>
            </div>
        );
    }

    // 🔧 FIX: Use summary netBalance from API (covers all transactions, not just paginated)
    // Fallback to rawEvents calculation only if summary not available
    const netCredits = usageData.historySummary?.netBalance != null
        ? usageData.historySummary.netBalance
        : usageData.rawEvents.reduce((sum, event) => {
            if (event.tokens && typeof event.tokens === 'number') {
                return sum + event.tokens;
            }
            return sum;
        }, 0);

    // 🔧 FIX: Use analytics.current as primary source (has full period data)
    const totalRequests = usageData.analytics?.current?.totalOperations
        || usageData.totalTransactions
        || usageData.rawEvents.length;

    // 🔧 FIX: Use correct DB transaction types: 'spend', 'credit', 'grant' (not 'purchase', 'bonus')
    // Priority: analytics.current > historySummary > rawEvents fallback
    const successfulRequests = usageData.analytics?.current?.totalOperations != null
        ? Math.round((usageData.analytics.current.successRate || 100) * usageData.analytics.current.totalOperations / 100)
        : (usageData.historySummary?.byType?.spend?.count || 0)
        + (usageData.historySummary?.byType?.credit?.count || 0)
        + (usageData.historySummary?.byType?.grant?.count || 0)
        + (usageData.historySummary?.byType?.refund?.count || 0);

    const erroredRequests = usageData.analytics?.current?.totalOperations != null
        ? Math.max(0, usageData.analytics.current.totalOperations - successfulRequests)
        : usageData.historySummary?.byType?.error?.count || 0;
    const purchaseRequests = usageData.rawEvents.filter(event => event.kind === 'Purchase').length;

    return (
        <div className="space-y-6 md:space-y-8">
            {/* Included Usage Summary */}
            <Card className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                    <Heading level={2}>Included Usage Summary</Heading>
                    <Button
                        onClick={() => { onRefresh(); loadUsageData(); }}
                        variant="ghost"
                        size="sm"
                        className="self-start sm:self-auto"
                    >
                        Refresh
                    </Button>
                </div>

                <div className="mb-4">
                    <Text size="sm" variant="secondary" className="mb-2">
                        Current billing period: {usageData.currentBillingPeriod.start} - {usageData.currentBillingPeriod.end}
                    </Text>

                    {/* ✅ FULLY DYNAMIC PLAN MESSAGING */}
                    <Text size="sm" className="mb-1">
                        <span className="font-bold capitalize">{usageData.plan.name || 'Current'}</span> plan
                        {usageData.plan.monthlyCredits ? (
                            <span> includes {usageData.plan.monthlyCredits.toLocaleString()} credits per month</span>
                        ) : usageData.plan.includedValue > 0 ? (
                            <span> comes with at least ${usageData.plan.includedValue} of included usage per month</span>
                        ) : (
                            <span> - Track your usage and optimize your costs</span>
                        )}.
                        {/* Dynamic upgrade suggestion based on current plan */}
                        {usageData.plan.id === 'free' && (
                            <span> Consider upgrading to Starter (5 architectures) or Pro (15 architectures) for more features.</span>
                        )}
                        {usageData.plan.id === 'starter' && (
                            <span> Upgrade to Pro (15 architectures) for even more capacity and priority support.</span>
                        )}
                        {usageData.plan.id === 'pro' && (
                            <span> You have access to our highest tier features and priority support.</span>
                        )}
                    </Text>

                    <Text size="sm" variant="secondary">
                        {usageData.autoUsageNote}
                    </Text>
                </div>

                {/* Timeframe Selection */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {usageData.timeframes.map((timeframe) => (
                        <Button
                            key={timeframe.value}
                            onClick={() => setSelectedTimeframe(timeframe.value)}
                            variant={selectedTimeframe === timeframe.value ? "primary" : "secondary"}
                            size="sm"
                            className="min-w-[56px] justify-center"
                        >
                            {timeframe.label}
                        </Button>
                    ))}
                </div>

                {/* Custom Date Range Picker */}
                {selectedTimeframe === 'custom' && (
                    <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
                        <div className="flex items-center gap-2">
                            <Text size="sm" variant="secondary">From:</Text>
                            <input
                                type="date"
                                value={dateRange.start}
                                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                                className="bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:border-[#7B61FF] focus:outline-none transition-colors"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Text size="sm" variant="secondary">To:</Text>
                            <input
                                type="date"
                                value={dateRange.end}
                                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                                className="bg-black/50 border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:border-[#7B61FF] focus:outline-none transition-colors"
                            />
                        </div>
                        <Button
                            onClick={() => loadUsageData(1)}
                            variant="primary"
                            size="sm"
                            disabled={!dateRange.start || !dateRange.end}
                        >
                            Apply
                        </Button>
                    </div>
                )}

                {/* Usage Statistics from Real Data */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard
                        value={(totalRequests || 0).toLocaleString()}
                        label="Total Requests"
                        accent="purple"
                    />
                    <MetricCard
                        value={(successfulRequests || 0).toLocaleString()}
                        label="Successful"
                        accent="purple"
                    />
                    <MetricCard
                        value={(erroredRequests || 0).toLocaleString()}
                        label="Errors"
                        accent="purple"
                    />
                    <MetricCard
                        value={(netCredits || 0).toLocaleString()}
                        label="Net Credits"
                        accent="purple"
                    />
                </div>

                {/* Analytics Insights */}
                {usageData.analytics && (
                    <InternalSection className="mt-6">
                        <Heading level={3} size="sm" className="mb-2">Analytics Insights</Heading>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                                <Text variant="secondary">Current Period Credits</Text>
                                <Text weight="bold">
                                    {(usageData.analytics.current?.totalCredits || 0).toLocaleString()}
                                </Text>
                            </div>
                            <div>
                                <Text variant="secondary">Operations Count</Text>
                                <Text weight="bold">
                                    {(usageData.analytics.current?.totalOperations || 0).toLocaleString()}
                                </Text>
                            </div>
                            <div>
                                <Text variant="secondary">Success Rate</Text>
                                <Text weight="bold">
                                    {usageData.analytics.current?.successRate?.toFixed(1) || 0}%
                                </Text>
                            </div>
                        </div>

                        {/* Show plan-specific insights */}
                        {usageData.plan.id !== 'free' && usageData.analytics.current?.operationBreakdown && (
                            <div className="mt-3 pt-3 border-t border-[#3A3D46]">
                                <Text size="xs" className="text-[#7B61FF] mb-1">Top Operation Types:</Text>
                                <div className="text-xs">
                                    {Object.entries(usageData.analytics.current.operationBreakdown)
                                        .sort(([, a], [, b]) => (b.count || 0) - (a.count || 0))
                                        .slice(0, 3)
                                        .map(([type, data]) => (
                                            <Text key={type} size="xs" className="mr-3 text-[#7B61FF]">
                                                {type}: {data.count || 0}
                                            </Text>
                                        ))
                                    }
                                </div>
                            </div>
                        )}
                    </InternalSection>
                )}
            </Card>

            {/* All Raw Events */}
            <Card>
                <div className="p-6 border-b border-[#3A3D46]">
                    <div className="flex items-center justify-between mb-4">
                        <Heading level={3}>All Transactions</Heading>
                        <div className="flex items-center space-x-2">
                            <Text size="sm" variant="secondary">
                                Showing {((currentPage - 1) * transactionsPerPage) + 1}-{Math.min(currentPage * transactionsPerPage, usageData.totalTransactions || usageData.rawEvents.length)} of {usageData.totalTransactions || usageData.rawEvents.length} transactions
                            </Text>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full table-fixed sm:table-auto">
                        <thead className="bg-[#2A2D36]">
                            <tr>
                                <th className="px-3 sm:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-[#CCCCCC] uppercase tracking-wider w-40 sm:w-auto">
                                    {getTimezoneDisplayName()}
                                </th>
                                <th className="px-3 sm:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-[#CCCCCC] uppercase tracking-wider w-20 sm:w-auto">
                                    User
                                </th>
                                <th className="px-3 sm:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-[#CCCCCC] uppercase tracking-wider w-28 sm:w-auto">
                                    Kind
                                </th>
                                <th className="px-3 sm:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-[#CCCCCC] uppercase tracking-wider w-16 sm:w-auto">
                                    Max
                                </th>
                                <th className="px-3 sm:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-[#CCCCCC] uppercase tracking-wider w-28 sm:w-auto">
                                    Provider
                                </th>
                                <th className="px-3 sm:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-[#CCCCCC] uppercase tracking-wider w-28 sm:w-auto">
                                    Model
                                </th>
                                <th className="px-3 sm:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-[#CCCCCC] uppercase tracking-wider w-24 sm:w-auto">
                                    Credits
                                </th>
                                <th className="px-3 sm:px-6 py-3 text-left text-[10px] sm:text-xs font-medium text-[#CCCCCC] uppercase tracking-wider w-20 sm:w-auto">
                                    Cost
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-[#1A1A1A] divide-y divide-[#3A3D46]">
                            {usageData.rawEvents.map((event, index) => (
                                <tr key={index} className="hover:bg-[#2A2D36]">
                                    <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-white">
                                        {event.date}
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-white">
                                        {event.user}
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                                        <Badge variant="secondary" size="sm">
                                            {getKindText(event.kind)}
                                        </Badge>
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-white">
                                        {event.max}
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-[#CCCCCC] font-mono">
                                        {event.provider || inferProviderFromModel(event.model)}
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-[#CCCCCC] font-mono">
                                        {event.model}
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-white font-mono">
                                        {(event.tokens || 0).toLocaleString()}
                                    </td>
                                    <td className="px-3 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-white">
                                        {event.cost}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {(usageData.totalTransactions || usageData.rawEvents.length) > transactionsPerPage && (
                    <div className="p-6 border-t border-[#3A3D46] bg-[#0F1117]">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <Text size="sm" variant="secondary">
                                    Page {currentPage} of {Math.ceil((usageData.totalTransactions || usageData.rawEvents.length) / transactionsPerPage)}
                                </Text>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    variant="secondary"
                                    size="sm"
                                >
                                    Previous
                                </Button>
                                <div className="flex items-center space-x-1">
                                    {Array.from({ length: Math.ceil((usageData.totalTransactions || usageData.rawEvents.length) / transactionsPerPage) }, (_, i) => i + 1)
                                        .filter(page =>
                                            page === 1 ||
                                            page === Math.ceil((usageData.totalTransactions || usageData.rawEvents.length) / transactionsPerPage) ||
                                            Math.abs(page - currentPage) <= 2
                                        )
                                        .map((page, index, array) => (
                                            <React.Fragment key={page}>
                                                {index > 0 && array[index - 1] < page - 1 && (
                                                    <Text size="sm" variant="secondary" className="px-2">...</Text>
                                                )}
                                                <Button
                                                    onClick={() => setCurrentPage(page)}
                                                    variant={currentPage === page ? "primary" : "secondary"}
                                                    size="sm"
                                                    className="min-w-[32px] h-8"
                                                >
                                                    {page}
                                                </Button>
                                            </React.Fragment>
                                        ))
                                    }
                                </div>
                                <Button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil((usageData.totalTransactions || usageData.rawEvents.length) / transactionsPerPage)))}
                                    disabled={currentPage === Math.ceil((usageData.totalTransactions || usageData.rawEvents.length) / transactionsPerPage)}
                                    variant="secondary"
                                    size="sm"
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}